import { designTokens } from '@cinch-labs/design-system'
import { GoogleMap, Marker } from '@react-google-maps/api'
import { useCallback, useEffect, useState, useRef } from 'react'
import { StoreWithDistance } from '../../hooks/types'
import {
  getBounds,
  styles as mapStyles,
} from '../find-a-store/find-a-store-map/utils'
import { StoreDetailsCard } from './store-details-card'
import styles from './mobile-map-overlay.module.css'
import { SvgIcon } from '@cinch-labs/design-system'
import {
  EventNames,
  TrackingEventTypes,
  useAnalytics,
} from '@cinch-nx/shared-analytics'
import { useGoogleMapsLoader } from '../../hooks/use-google-maps-loader'

const {
  color: { vividViolet },
} = designTokens.customProperties.ds

const GREY_PIN_COLOR = '#9CA3AF'

interface MobileMapOverlayProps {
  stores: StoreWithDistance[]
  selectedStore?: StoreWithDistance
  onClose: () => void
  customerPostcode?: string
  onSelectStore: (store: StoreWithDistance) => void
}

const locationSvgPath =
  'M25.412 16.077c-1.185 3.283-4.63 9.502-6.634 11.864a3 3 0 0 1-4.782-.274c-2.822-4.224-4.677-7.416-6.169-10.87C5.303 10.95 8.945 4.46 14.551 3.21c.631-.14 1.27-.21 1.911-.21 4.288 0 8.127 3.117 9.223 7.371.497 1.93.406 3.823-.273 5.706zM22.78 11.12C22.02 8.17 19.358 6 16.462 6c-.416 0-.837.045-1.259.138-3.427.765-6.527 5.057-4.622 9.469 1.592 3.686 3.67 7.041 5.91 10.393 1.814-2.138 5.066-8.08 6.099-10.941.475-1.318.536-2.596.19-3.94zM16.5 15c-1.493 0-3.01-1.343-3.01-3s1.517-3 3.01-3c1.493 0 2.913 1.5 2.913 3s-1.42 3-2.913 3z'

export const MobileMapOverlay: React.FC<MobileMapOverlayProps> = ({
  stores,
  selectedStore: initialSelectedStore,
  onClose,
  customerPostcode,
  onSelectStore,
}) => {
  const { trackEvent } = useAnalytics()
  const { isLoaded } = useGoogleMapsLoader()
  const [map, setMap] = useState<google.maps.Map>()
  const [selectedStore, setSelectedStore] = useState<
    StoreWithDistance | undefined
  >(initialSelectedStore)
  const [isCardMinimized, setIsCardMinimized] = useState(false)
  const isProgrammaticMove = useRef(true)

  const onLoad = useCallback((map: google.maps.Map) => {
    setMap(map)

    setTimeout(() => {
      isProgrammaticMove.current = false
    }, 1000)
  }, [])

  const handleMapInteraction = useCallback(() => {
    if (isProgrammaticMove.current) {
      return
    }

    setIsCardMinimized((prev) => {
      if (selectedStore && !prev) {
        return true
      }
      return prev
    })
  }, [selectedStore])

  const markers = stores.map((s) => ({
    id: s.store.id,
    lat: s.store.coordinates.latitude,
    lng: s.store.coordinates.longitude,
    store: s,
  }))

  useEffect(() => {
    if (!map || !selectedStore) {
      return
    }

    isProgrammaticMove.current = true

    map.setZoom(12)
    map.panTo({
      lat: selectedStore.store.coordinates.latitude,
      lng: selectedStore.store.coordinates.longitude,
    })

    const timeoutId = setTimeout(() => {
      isProgrammaticMove.current = false
    }, 600)

    return () => clearTimeout(timeoutId)
  }, [map, selectedStore])

  useEffect(() => {
    if (!map || selectedStore) {
      return
    }

    isProgrammaticMove.current = true

    const collectionSiteBounds = getBounds(markers)
    map.fitBounds(collectionSiteBounds)

    const timeoutId = setTimeout(() => {
      isProgrammaticMove.current = false
    }, 600)

    return () => clearTimeout(timeoutId)
  }, [map, stores, selectedStore])

  const onMarkerClick = (
    store: StoreWithDistance,
    event: google.maps.MapMouseEvent,
  ) => {
    if (event.domEvent) {
      event.domEvent.stopPropagation()
    }

    trackEvent({
      eventName: `Mobile Store Marker`,
      type: TrackingEventTypes.ADOBE,
      data: {
        event: {
          name: EventNames.click,
          category: 'cinch_servicing',
          action: 'mobile_store_marker_click',
          label: store.store.name,
        },
      },
    })
    setSelectedStore(store)
    setIsCardMinimized(false)
  }

  const handleCloseMap = () => {
    trackEvent({
      eventName: `Close Mobile Map`,
      type: TrackingEventTypes.ADOBE,
      data: {
        event: {
          name: EventNames.click,
          category: 'cinch_servicing',
          action: 'close_mobile_map',
        },
      },
    })
    onClose()
  }

  if (!isLoaded) {
    return null
  }

  return (
    <div className={styles.overlay}>
      <div className={styles.header}>
        <button
          className={styles.closeButton}
          onClick={handleCloseMap}
          aria-label="Close map"
        >
          <div className={styles.closeIconCircle}>
            <SvgIcon name="cross" />
          </div>
          <div className={styles.closeTextContainer}>
            <span className={styles.closeText}>Close map</span>
          </div>
        </button>
      </div>

      <GoogleMap
        mapContainerStyle={{
          height: '100%',
          width: '100%',
        }}
        options={{
          mapTypeControl: false,
          clickableIcons: false,
          styles: mapStyles,
          streetViewControl: false,
          fullscreenControl: false,
          zoomControl: false,
          gestureHandling: 'greedy',
        }}
        onLoad={onLoad}
        zoom={9}
        onDrag={handleMapInteraction}
        onZoomChanged={handleMapInteraction}
      >
        {markers.map((marker) => {
          const isSelected = selectedStore?.store.id === marker.id
          return (
            <Marker
              title={marker.store.store.name}
              icon={
                {
                  size: { width: 7, height: 6 },
                  anchor: { x: 19 - 7, y: 19 + 6 },
                  fillColor: isSelected ? vividViolet : GREY_PIN_COLOR,
                  fillOpacity: 1,
                  strokeColor: isSelected ? vividViolet : GREY_PIN_COLOR,
                  path: locationSvgPath,
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                } as any
              }
              key={marker.id}
              position={marker}
              onClick={(e) => onMarkerClick(marker.store, e)}
            />
          )
        })}
      </GoogleMap>

      {selectedStore && (
        <StoreDetailsCard
          store={selectedStore}
          isMinimized={isCardMinimized}
          onMinimize={() => setIsCardMinimized(true)}
          onExpand={() => setIsCardMinimized(false)}
          customerPostcode={customerPostcode}
          onSelectStore={() => onSelectStore(selectedStore)}
        />
      )}
    </div>
  )
}

export default MobileMapOverlay
